import dayjs, { type Dayjs } from 'dayjs';

/**
 * API çoğunlukla UTC anını ofsetsiz ISO benzeri string olarak döner.
 * Tarayıcı bu stringi çoğu zaman *yerel* saat gibi yorumlar; `Z` / ofset ekleyerek UTC parse edip
 * `toLocale*` / `dayjs` ile kullanıcının yerel saatine çeviririz.
 */

const HAS_OFFSET_OR_Z_RE = /[zZ]|[+-]\d{2}:?\d{2}$/;

/** Ofset yoksa sonuna `Z` eklenir; `YYYY-MM-DD HH:mm:ss` → `T` ile birleştirilir. */
export function normalizeApiDateTimeToIso(raw: string): string {
  const s = String(raw).trim();
  if (!s) return s;
  const withT = s.includes('T') ? s : s.replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T');
  if (HAS_OFFSET_OR_Z_RE.test(withT)) return withT;
  return `${withT}Z`;
}

/** OpenAPI FromJSON: `json['createdAt']` vb. için güvenli Date üretimi. */
export function parseJsonDateAsUtc(value: unknown): Date | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return undefined;
    const d = new Date(normalizeApiDateTimeToIso(s));
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

export function parseApiUtcToLocalDate(
  value: string | Date | number | null | undefined
): Date | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const d = new Date(normalizeApiDateTimeToIso(String(value).trim()));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function getApiUtcTimeMs(value: string | Date | number | null | undefined): number {
  return parseApiUtcToLocalDate(value)?.getTime() ?? 0;
}

export function formatApiUtcToLocaleDate(
  value: string | Date | number | null | undefined,
  locales: Intl.LocalesArgument = 'tr-TR',
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }
): string {
  const d = parseApiUtcToLocalDate(value);
  if (!d) return '-';
  try {
    return d.toLocaleDateString(locales, options);
  } catch {
    return '-';
  }
}

export function formatApiUtcToLocaleDateTime(
  value: string | Date | number | null | undefined,
  locales: Intl.LocalesArgument = 'tr-TR',
  options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
): string {
  const d = parseApiUtcToLocalDate(value);
  if (!d) return '-';
  try {
    return d.toLocaleString(locales, options);
  } catch {
    return '-';
  }
}

export function formatApiUtcToLocaleTime(
  value: string | Date | number | null | undefined,
  locales: Intl.LocalesArgument = 'tr-TR',
  options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', second: '2-digit' }
): string {
  const d = parseApiUtcToLocalDate(value);
  if (!d) return '-';
  try {
    return d.toLocaleTimeString(locales, options);
  } catch {
    return '-';
  }
}

/** `relativeTime` vb. plugin’ler uygulama girişinde dayjs’e eklendiyse bu örnek de etkilenir. */
export function dayjsFromApiUtc(input: string | Date | number | null | undefined): Dayjs {
  if (input == null || input === '') return dayjs(NaN);
  if (typeof input === 'number') return dayjs(input);
  if (input instanceof Date) return dayjs(input);
  return dayjs(normalizeApiDateTimeToIso(String(input).trim()));
}
